import { reportError } from "utils/diagnostics";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { NxtIconButton } from "components/common/nxt-icon-button/nxt-icon-button.component";
import { PageTitle } from "components/common/Text";
import TableFilterNew from "components/table/TableFilterNew.component";
import style from "./device-lookup.module.css";
import CancelPresentationOutlinedIcon from "@mui/icons-material/CancelPresentationOutlined";
import {
  DEVICE_LOOKUP_COLUMNS,
  ORGANIZATION_DEVICE_LOOKUP_COLUMNS,
  DEVICE_LOOKUP_OPTIONS,
  DeviceDataTypes,
} from "./device-lookup.config";
import useConfirm from "hooks/useConfirm";
import { showSuccess } from "utils/notifications";
import { deleteDevice } from "api/DeviceHelpers";
import { TableRow, Typography } from "@mui/material";
import CustomTable from "components/table/table.component";
import { TableBodyCell } from "components/table/table.style";
import { getDeviceByImei, getDeviceBySerial, getDevicesByOrganization } from "./device-lookup.api";
import { Spinner } from "components/common/spinner/spinner.component";
import { Popup } from "components/common/popup/popup.component";
import CertificateRefreshPanel from "./certificate-refresh.component";

export default function DeviceLookupPage(): JSX.Element {
  const [deviceData, setDeviceData] = useState<DeviceDataTypes[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [query, setQuery] = useState<{ column: string; word: string } | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const rowsPerPageRef = useRef(10);
  const [totalCount, setTotalCount] = useState(0);
  const requestId = useRef(0);
  const isOrganizationLookup = query?.column === "orgGroup";
  const columns = isOrganizationLookup ? ORGANIZATION_DEVICE_LOOKUP_COLUMNS : DEVICE_LOOKUP_COLUMNS;

  useEffect(
    () => () => {
      requestId.current += 1;
    },
    [],
  );

  const filterColumns = useMemo(() => DEVICE_LOOKUP_OPTIONS.filter(({ filter }) => filter), []);
  const [getConfirmation, Confirmation] = useConfirm();

  const loadDevices = async (column: string, word: string, nextPage = 0, pageSize = rowsPerPageRef.current) => {
    const currentRequest = ++requestId.current;
    setIsLoading(true);
    setQuery({ column, word });
    setPage(nextPage);
    setRowsPerPage(pageSize);
    rowsPerPageRef.current = pageSize;
    setDeviceData([]);
    setTotalCount(0);
    try {
      if (column === "orgGroup") {
        const response = await getDevicesByOrganization(word, nextPage, pageSize);
        if (requestId.current !== currentRequest) return;
        setDeviceData(response.devices);
        setTotalCount(response.totalCount);
        return;
      }
      const response = await (column === "serialNumber" ? getDeviceBySerial(word) : getDeviceByImei(word));
      if (requestId.current === currentRequest && response) setDeviceData([response]);
    } catch (error: any) {
      reportError("pages/device-lookup/device-lookup.page.onSearch", error);
    } finally {
      if (requestId.current === currentRequest) setIsLoading(false);
    }
  };

  const onResetFilter = () => {
    requestId.current += 1;
    setQuery(null);
    setDeviceData([]);
    setTotalCount(0);
    setPage(0);
    setIsLoading(false);
  };

  const onSearch = (column: string, columnType: string, searchContent: string) => {
    const word = searchContent.trim();
    if (word) loadDevices(column, word);
    else onResetFilter();
  };

  const onRemoveDevice = async () => {
    if (!isLoading && !isOrganizationLookup && deviceData.length === 1) {
      const currentRequest = requestId.current;
      const { serialNumber } = deviceData[0];
      const isConfirm = await getConfirmation({
        title: "Delete Device?",
        content: "Are you sure to delete selected devices?",
        buttonTxt: "Delete",
      });

      if (isConfirm && requestId.current === currentRequest) {
        try {
          setIsLoading(true);
          await deleteDevice(serialNumber);
          if (requestId.current === currentRequest) setDeviceData([]);
          showSuccess(`Successfully removed device: ${serialNumber}`);
        } catch (error) {
          reportError("pages/device-lookup/device-lookup.page.onRemoveDevice", error);
        } finally {
          if (requestId.current === currentRequest) setIsLoading(false);
        }
      }
    }
  };

  const getTableValue = (column: any, value: any) => {
    if (column.id === "online") return value ? "Online" : "Offline";
    if (column.id === "locationTrailEnabled") {
      if (!value) {
        return "Disabled";
      }
      return "Enabled";
    }

    if (column.format) {
      return column.format(value);
    }
    return value || "-";
  };

  const onPageChange = (nextPage: number) => {
    if (query && isOrganizationLookup) loadDevices(query.column, query.word, nextPage);
  };

  const onRowsPerPageChange = (pageSize: number) => {
    // CustomTable calls onPageChange(0) immediately after this callback.
    rowsPerPageRef.current = pageSize;
    setRowsPerPage(pageSize);
  };

  return (
    <>
      <PageTitle>Device Lookup</PageTitle>

      <div className={style.barContainer}>
        <div className={style.filterContainer}>
          <TableFilterNew columns={filterColumns} onSearch={onSearch} onReset={onResetFilter} />
        </div>
        <div className={style.buttonOptions}>
          <NxtIconButton
            icon={<CancelPresentationOutlinedIcon />}
            disabled={isLoading || isOrganizationLookup || deviceData.length !== 1}
            onClick={onRemoveDevice}
            text="Detach Device"
          />
        </div>
      </div>

      <Typography variant="body2" sx={{ mb: 2 }}>
        Search by the exact serial number, IMEI or organization name. Organization results are read-only.
      </Typography>

      <CustomTable
        boxShadow
        totalCount={totalCount}
        page={page}
        rowsPerPage={rowsPerPage}
        rowsPerPageOptions={isOrganizationLookup ? [10, 25, 100] : undefined}
        onPageChange={onPageChange}
        onRowsPerPageChange={onRowsPerPageChange}
      >
        <CustomTable.Header columns={columns.map((column) => ({ ...column, isSort: false }))} />

        <CustomTable.Body isLoading={isLoading} deviceData={deviceData}>
          {deviceData &&
            deviceData.map(
              (row: DeviceDataTypes) =>
                row && (
                  <TableRow key={row.serialNumber}>
                    {columns.map((column) => {
                      const value = row[column.id as keyof DeviceDataTypes];

                      return <TableBodyCell key={column.id}>{getTableValue(column, value)}</TableBodyCell>;
                    })}
                  </TableRow>
                ),
            )}
        </CustomTable.Body>
      </CustomTable>

      {!isOrganizationLookup && !isLoading && deviceData.length === 1 && deviceData[0]?.serialNumber && (
        <CertificateRefreshPanel key={deviceData[0].serialNumber} serialNumber={deviceData[0].serialNumber} />
      )}

      <Popup title="Updating data..." open={isLoading}>
        <Spinner />
      </Popup>

      <Confirmation />
    </>
  );
}
